import React, { useEffect, useState, useRef } from "react";
import { api } from "../lib/api";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import { toast } from "sonner";
import { Target, ChartLineUp, Lightning, Crown, X, ArrowRight } from "@phosphor-icons/react";

const DIFF_COLORS = {
  easy: "text-emerald-400 border-emerald-400/40",
  medium: "text-amber-400 border-amber-400/40",
  hard: "text-red-400 border-red-400/40",
};

const GRADE_COLORS = {
  A: "text-emerald-400", "A-": "text-emerald-400", "B+": "text-lime-400", B: "text-lime-400",
  "B-": "text-amber-300", "C+": "text-amber-400", C: "text-orange-400", "C-": "text-orange-500",
  D: "text-red-400", F: "text-red-500",
};

export default function CloserLab() {
  const [tab, setTab] = useState("scenarios");
  const [scenarios, setScenarios] = useState([]);
  const [stats, setStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [cards, setCards] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [accessError, setAccessError] = useState(null);

  useEffect(() => {
    api.get("/closer/scenarios").then((r) => setScenarios(r.data.scenarios || []))
      .catch((e) => setAccessError(e?.response?.status === 403 ? "Closer Lab access requires sales or advisor login." : null));
    api.get("/closer/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/closer/leaderboard").then((r) => setLeaderboard(r.data.leaderboard || [])).catch(() => {});
    api.get("/closer/cashcards").then((r) => setCards(r.data.cards || [])).catch(() => {});
  }, []);

  if (accessError) {
    return (
      <div className="min-h-screen bg-exodus-bg text-exodus-text">
        <Nav />
        <div className="max-w-2xl mx-auto px-6 pt-36 text-center">
          <Crown size={48} className="mx-auto text-exodus-gold opacity-50" />
          <h1 className="font-display text-3xl mt-6">Closer Lab is Locked</h1>
          <p className="text-exodus-muted mt-4">{accessError}</p>
          <p className="text-xs text-exodus-muted mt-2">Ask Leon for a sales-role login if you&apos;re on the team.</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-exodus-bg text-exodus-text">
      <Nav />
      <div className="max-w-6xl mx-auto px-6 lg:px-12 pt-32 pb-24">
        <div className="mb-8">
          <p className="eyebrow"><span className="gold-divider" />Closer Lab · Sales Training</p>
          <h1 className="font-display text-4xl mt-3">Drill the close until objections feel predictable.</h1>
          <p className="text-sm text-exodus-muted mt-3 max-w-2xl">
            Roleplay every realistic prospect objection. Get AI-scored A through F on Discovery, Objection Handling, Close, and Tonality. Track your progression. Outrank the team.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-exodus-border mb-8 overflow-x-auto">
          {[
            ["scenarios", "Scenarios", Target],
            ["cashcards", "Cash Cards", Lightning],
            ["progress", "My Progress", ChartLineUp],
            ["leaderboard", "Leaderboard", Crown],
          ].map(([key, label, Icon]) => (
            <button
              key={key}
              data-testid={`closer-tab-${key}`}
              onClick={() => setTab(key)}
              className={`px-5 py-3 text-sm font-medium flex items-center gap-2 transition-colors whitespace-nowrap border-b-2 ${
                tab === key ? "text-exodus-gold border-exodus-gold" : "text-exodus-muted border-transparent hover:text-exodus-text"
              }`}
            >
              <Icon size={16} /> {label}
            </button>
          ))}
        </div>

        {tab === "scenarios" && (
          <ScenarioGrid scenarios={scenarios} onStart={setActiveSession} />
        )}
        {tab === "cashcards" && <CashCardGrid cards={cards} />}
        {tab === "progress" && <ProgressView stats={stats} />}
        {tab === "leaderboard" && <LeaderboardView entries={leaderboard} />}
      </div>

      {activeSession && (
        <RoleplayModal session={activeSession} onClose={() => {
          setActiveSession(null);
          api.get("/closer/stats").then((r) => setStats(r.data)).catch(() => {});
          api.get("/closer/leaderboard").then((r) => setLeaderboard(r.data.leaderboard || [])).catch(() => {});
        }} />
      )}

      <Footer />
    </div>
  );
}

function ScenarioGrid({ scenarios, onStart }) {
  const start = async (id) => {
    try {
      const r = await api.post("/closer/roleplay/start", { scenario_id: id });
      onStart(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start session");
    }
  };
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
      {scenarios.map((s) => (
        <div key={s.id} className="border border-exodus-border bg-exodus-card-bg p-5 hover:border-exodus-gold/60 transition-colors" data-testid={`scenario-${s.id}`}>
          <div className="flex justify-between items-start mb-3">
            <span className={`text-[10px] uppercase tracking-wider border px-2 py-0.5 ${DIFF_COLORS[s.difficulty] || ""}`}>
              {s.difficulty}
            </span>
            <span className="text-[10px] uppercase text-exodus-muted tracking-wider">{s.category}</span>
          </div>
          <h3 className="font-display text-lg mb-2">{s.title}</h3>
          <p className="text-xs text-exodus-muted mb-1">{s.prospect?.name} · {s.prospect?.occupation}</p>
          <p className="text-sm italic text-exodus-text/80 mb-4 line-clamp-3">&quot;{s.stated_objection}&quot;</p>
          <button data-testid={`start-${s.id}`} onClick={() => start(s.id)} className="btn-gold text-xs flex items-center gap-1 w-full justify-center">
            Start Roleplay <ArrowRight size={14} />
          </button>
        </div>
      ))}
    </div>
  );
}

function RoleplayModal({ session, onClose }) {
  const [messages, setMessages] = useState([{ role: "prospect", text: session.opening_line }]);
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const [ending, setEnding] = useState(false);
  const [finalScore, setFinalScore] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => { scrollRef.current?.scrollTo(0, 999999); }, [messages, thinking]);

  const send = async () => {
    const text = input.trim();
    if (!text || thinking) return;
    setMessages((m) => [...m, { role: "rep", text }]);
    setInput("");
    setThinking(true);
    try {
      const r = await api.post("/closer/roleplay/turn", { session_id: session.session_id, rep_message: text });
      setMessages((m) => [...m, { role: "prospect", text: r.data.prospect_reply }]);
    } catch (e) {
      toast.error("Roleplay turn failed");
    } finally { setThinking(false); }
  };

  const end = async () => {
    setEnding(true);
    try {
      const r = await api.post("/closer/roleplay/end", { session_id: session.session_id });
      setFinalScore(r.data.score);
    } catch (e) {
      toast.error("Scoring failed");
      setEnding(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-exodus-bg border border-exodus-border max-w-3xl w-full max-h-[92vh] flex flex-col" data-testid="roleplay-modal">
        <div className="flex justify-between items-start p-5 border-b border-exodus-border">
          <div>
            <p className="eyebrow text-[10px]"><span className="gold-divider" />Live Roleplay</p>
            <h2 className="font-display text-xl mt-1">{session.scenario.title}</h2>
            <p className="text-xs text-exodus-muted mt-1">
              {session.scenario.prospect?.name} · {session.scenario.prospect?.occupation} · {session.scenario.prospect?.tone}
            </p>
          </div>
          <button data-testid="roleplay-close" onClick={onClose} className="text-exodus-muted hover:text-exodus-text"><X size={20} /></button>
        </div>

        {!finalScore ? (
          <>
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "rep" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] px-4 py-2.5 text-sm ${m.role === "rep" ? "bg-exodus-gold text-exodus-bg" : "bg-exodus-card-bg border border-exodus-border"}`}>
                    {m.text}
                  </div>
                </div>
              ))}
              {thinking && (
                <div className="text-xs text-exodus-muted italic">{session.scenario.prospect?.name} is thinking…</div>
              )}
            </div>
            <div className="border-t border-exodus-border p-4 flex gap-2">
              <input
                data-testid="roleplay-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())}
                placeholder="Type your response…"
                className="flex-1 bg-exodus-card-bg border border-exodus-border px-3 py-2 text-sm focus:border-exodus-gold outline-none"
                disabled={thinking || ending}
              />
              <button data-testid="roleplay-send" onClick={send} disabled={thinking || !input.trim()} className="btn-gold text-xs disabled:opacity-40">Send</button>
              <button data-testid="roleplay-end" onClick={end} disabled={ending || messages.length < 3} className="btn-ghost text-xs disabled:opacity-40">
                {ending ? "Scoring…" : "End & Score"}
              </button>
            </div>
          </>
        ) : (
          <ScoreView score={finalScore} scenario={session.scenario} onClose={onClose} />
        )}
      </div>
    </div>
  );
}

function ScoreView({ score, scenario, onClose }) {
  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="text-center mb-8">
        <p className="text-xs text-exodus-muted uppercase tracking-wider">Your Score</p>
        <h2 className={`font-display text-7xl mt-2 ${GRADE_COLORS[score.overall_grade] || "text-exodus-gold"}`}>{score.overall_grade}</h2>
        <p className="text-sm text-exodus-muted mt-1">Average: {score.average}/100</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[
          ["Discovery", score.discovery],
          ["Objections", score.objection_handling],
          ["Close", score.close],
          ["Tonality", score.tonality],
        ].map(([label, val]) => (
          <div key={label} className="border border-exodus-border p-3 text-center">
            <p className="text-[10px] uppercase tracking-wider text-exodus-muted">{label}</p>
            <p className="font-display text-2xl mt-1">{val}</p>
          </div>
        ))}
      </div>
      {score.strengths?.length > 0 && (
        <div className="mb-4">
          <p className="text-xs uppercase tracking-wider text-emerald-400 mb-2">Strengths</p>
          <ul className="text-sm text-exodus-text/90 space-y-1 list-disc pl-5">
            {score.strengths.map((s, i) => <li key={i}>{s}</li>)}
          </ul>
        </div>
      )}
      {score.weaknesses?.length > 0 && (
        <div className="mb-4">
          <p className="text-xs uppercase tracking-wider text-amber-400 mb-2">Watch-outs</p>
          <ul className="text-sm text-exodus-text/90 space-y-1 list-disc pl-5">
            {score.weaknesses.map((s, i) => <li key={i}>{s}</li>)}
          </ul>
        </div>
      )}
      {score.ideal_response_at_critical_moment && (
        <div className="bg-exodus-card-bg border-l-2 border-exodus-gold p-4 mb-6">
          <p className="text-xs uppercase tracking-wider text-exodus-gold mb-2">What you should&apos;ve said</p>
          <p className="text-sm italic">&quot;{score.ideal_response_at_critical_moment}&quot;</p>
        </div>
      )}
      <button onClick={onClose} className="btn-gold w-full">Close</button>
    </div>
  );
}

function CashCardGrid({ cards }) {
  const [flipped, setFlipped] = useState({});
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
      {cards.map((c) => (
        <div key={c.id} onClick={() => setFlipped((f) => ({ ...f, [c.id]: !f[c.id] }))} className="border border-exodus-border bg-exodus-card-bg p-5 cursor-pointer hover:border-exodus-gold/60 transition-colors min-h-[180px]" data-testid={`cashcard-${c.id}`}>
          <div className="flex justify-between text-[10px] uppercase tracking-wider mb-3">
            <span className={`border px-2 py-0.5 ${DIFF_COLORS[c.difficulty] || ""}`}>{c.difficulty}</span>
            <span className="text-exodus-muted">{c.category}</span>
          </div>
          {!flipped[c.id] ? (
            <>
              <p className="text-xs text-exodus-muted mb-2">{c.context}</p>
              <p className="text-base italic">&quot;{c.objection}&quot;</p>
              <p className="text-[10px] text-exodus-gold mt-4 uppercase tracking-wider">Tap to reveal response →</p>
            </>
          ) : (
            <>
              <p className="text-xs text-exodus-gold uppercase tracking-wider mb-2">Leon&apos;s Move</p>
              <p className="text-sm">{c.ideal_response}</p>
            </>
          )}
        </div>
      ))}
    </div>
  );
}

function ProgressView({ stats }) {
  if (!stats || stats.sessions_completed === 0) {
    return <p className="text-sm text-exodus-muted">No completed sessions yet. Drill some scenarios to start building your skill profile.</p>;
  }
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Sessions" value={stats.sessions_completed} />
        <Kpi label="Avg Score" value={`${stats.average_score}/100`} />
        <Kpi label="Best Grade" value={stats.best_grade} grade={stats.best_grade} />
        <Kpi label="Strongest" value={Object.entries(stats.by_dimension || {}).sort((a, b) => b[1] - a[1])[0]?.[0]?.replace(/_/g, " ") || "—"} />
      </div>
      <div>
        <h3 className="font-display text-xl mb-3">Skill Breakdown</h3>
        <div className="space-y-2">
          {Object.entries(stats.by_dimension || {}).map(([k, v]) => (
            <div key={k} className="flex items-center gap-3">
              <span className="text-xs text-exodus-muted uppercase tracking-wider w-32">{k.replace(/_/g, " ")}</span>
              <div className="flex-1 bg-exodus-card-bg h-3 overflow-hidden">
                <div className="bg-exodus-gold h-full" style={{ width: `${v}%` }} />
              </div>
              <span className="text-xs font-mono w-12 text-right">{v}</span>
            </div>
          ))}
        </div>
      </div>
      <div>
        <h3 className="font-display text-xl mb-3">Recent Sessions</h3>
        <div className="space-y-2">
          {(stats.recent || []).map((r, i) => (
            <div key={i} className="border border-exodus-border p-3 flex justify-between items-center">
              <div>
                <p className="text-sm">{r.scenario_title}</p>
                <p className="text-[10px] text-exodus-muted">{r.completed_at?.slice(0, 10)}</p>
              </div>
              <span className={`font-display text-2xl ${GRADE_COLORS[r.grade] || "text-exodus-gold"}`}>{r.grade}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function LeaderboardView({ entries }) {
  if (!entries.length) return <p className="text-sm text-exodus-muted">No sessions yet across the team. Be the first to drop a score.</p>;
  return (
    <div className="border border-exodus-border">
      {entries.map((e, i) => (
        <div key={e.email} className="flex items-center gap-4 p-4 border-b border-exodus-border last:border-b-0">
          <span className="font-display text-2xl w-10 text-exodus-gold">{i + 1}</span>
          <div className="flex-1">
            <p className="text-sm">{e.name}</p>
            <p className="text-[10px] text-exodus-muted">{e.sessions} sessions</p>
          </div>
          <span className={`font-display text-xl ${GRADE_COLORS[e.best_grade] || ""}`}>{e.best_grade}</span>
          <span className="text-sm font-mono w-16 text-right">{e.avg_score}</span>
        </div>
      ))}
    </div>
  );
}

function Kpi({ label, value, grade }) {
  return (
    <div className="border border-exodus-border p-4">
      <p className="text-[10px] uppercase tracking-wider text-exodus-muted">{label}</p>
      <p className={`font-display text-3xl mt-1 ${grade ? GRADE_COLORS[grade] || "" : ""}`}>{value}</p>
    </div>
  );
}
